import { useRef, useState, type FormEvent } from 'react';
import type { Me } from '@blackblack/shared';
import { USERNAME_RE } from '@blackblack/shared';
import { api, ApiError, errorMessage, uploadFile } from '../../lib/api';
import { invalidate } from '../../lib/query';
import { goBack } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Avatar, Button, TextArea, TextField } from '../../components/ui/primitives';
import { Page, PageHeader } from '../../components/layout';
import styles from './profile.module.css';

export function EditProfilePage() {
  const { me, setMe } = useAuth();
  const toast = useToast();
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [username, setUsername] = useState(me?.username ?? '');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [avatar, setAvatar] = useState<{ file: File; url: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  if (!me) return null;

  const usernameValid = USERNAME_RE.test(username.toLowerCase());

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!usernameValid) return setErrors({ username: '2–24 tekens: a-z, 0-9, _ en .' });
    setBusy(true);
    setErrors({});
    try {
      const avatarUploadId = avatar ? (await uploadFile(avatar.file, 'image')).uploadId : undefined;
      const updated = await api.patch<Me>('/users/me', { displayName, username: username.toLowerCase(), bio, ...(avatarUploadId ? { avatarUploadId } : {}) });
      setMe(updated);
      invalidate('profile:');
      toast('Profiel bijgewerkt', { tone: 'success' });
      goBack(`/u/${updated.username}`);
    } catch (err) {
      if (err instanceof ApiError && err.details) setErrors(err.details);
      toast(errorMessage(err), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader title="Profiel bewerken" fallback="/me" />
      <form className={styles.form} onSubmit={save}>
        <button type="button" className={styles.avatarEdit} onClick={() => fileInput.current?.click()} aria-label="Profielfoto wijzigen">
          <Avatar user={{ displayName, avatarUrl: avatar?.url ?? me.avatarUrl }} size={104} />
          <span className={styles.avatarBadge}>
            <Icon name="camera" size={16} />
          </span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setAvatar({ file: f, url: URL.createObjectURL(f) });
          }}
        />
        <TextField label="Naam" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={50} />
        <TextField
          label="Gebruikersnaam"
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
          maxLength={24}
          error={errors.username ? (errors.username === 'taken' ? 'Deze gebruikersnaam is al bezet' : errors.username) : !usernameValid && username ? 'Alleen a-z, 0-9, _ en . (2–24 tekens)' : undefined}
          hint={`blackblack.app/u/${username || 'jouwnaam'}`}
          autoCapitalize="none"
          autoCorrect="off"
        />
        <TextArea label="Bio" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={200} rows={3} hint={`${bio.length}/200`} />
        <Button type="submit" block loading={busy}>
          Opslaan
        </Button>
      </form>
    </Page>
  );
}
